/**
 * Eine Leistungsart aufgeklappt.
 *
 * Zugeordnet wird ueber die Positions-ID (`serviceId` → `assignment`), nicht
 * ueber den Titel: gleichnamige Positionen in verschiedenen Projekten sind
 * nicht dieselbe Leistung, und umbenannte Positionen fielen vorher heraus.
 */
import React, { useMemo } from 'react';
import { ChevronRight } from 'lucide-react';
import { Button } from '../Button';
import type { CockpitModel, ProjectRow, ServiceTypeRow } from '../../services/cockpitTypes';
import { Bar, ModalShell } from './CockpitBits';
import { fmtCents, pctOf } from './cockpitUi';

export interface ServiceTypeDrilldownProps {
    serviceType: ServiceTypeRow;
    model: CockpitModel;
    /** Positions-ID → Zuordnung, aus den geladenen Projekten. */
    assignmentByService: Record<string, string>;
    hideAmounts: boolean;
    periodText: string;
    onSelectProject: (project: ProjectRow) => void;
    onClose: () => void;
}

export const ServiceTypeDrilldown: React.FC<ServiceTypeDrilldownProps> = ({
    serviceType, model, assignmentByService, hideAmounts, periodText, onSelectProject, onClose,
}) => {
    const money = (c: number) => fmtCents(c, hideAmounts);

    const entries = useMemo(
        () => model.performedDetails.filter((d) => assignmentByService[d.serviceId] === serviceType.key),
        [model.performedDetails, assignmentByService, serviceType.key],
    );

    const byPerformer = useMemo(() => {
        const map = new Map<string, { name: string; cents: number; quantity: number }>();
        entries.forEach((d) => {
            const key = d.performerId ?? 'unknown';
            const row = map.get(key) ?? { name: d.performerName || 'Ohne Zuordnung', cents: 0, quantity: 0 };
            row.cents += d.cents;
            row.quantity += d.quantity;
            map.set(key, row);
        });
        return [...map.values()].sort((a, b) => b.cents - a.cents);
    }, [entries]);

    const byProject = useMemo(() => {
        const map = new Map<string, number>();
        entries.forEach((d) => map.set(d.projectId, (map.get(d.projectId) ?? 0) + d.cents));
        return [...map.entries()]
            .map(([id, cents]) => ({ project: model.projects.find((p) => p.id === id), cents }))
            .filter((r): r is { project: ProjectRow; cents: number } => !!r.project)
            .sort((a, b) => b.cents - a.cents);
    }, [entries, model.projects]);

    const realisation = pctOf(serviceType.performedCents, serviceType.forecastCents);

    return (
        <ModalShell
            title={serviceType.label}
            width="max-w-3xl"
            onClose={onClose}
            subtitle={
                <>
                    <span className="text-[11px] text-slate-300">{serviceType.entryCount} Einträge</span>
                    <span className="text-[11px] text-emerald-300">Erbracht {money(serviceType.performedCents)}</span>
                    <span className="text-[11px] text-sky-300">Forecast {money(serviceType.forecastCents)}</span>
                </>
            }
            footer={<><span className="text-[11px] text-slate-400">Zeitraum {periodText}</span><Button variant="primary" size="sm" onClick={onClose}>Schließen</Button></>}
        >
            <div>
                <div className="flex justify-between text-[11px] font-semibold text-slate-500 mb-1">
                    <span>Realisierung</span><span>{realisation}%</span>
                </div>
                <Bar value={realisation} max={100} height="h-2" color="bg-emerald-500" />
            </div>

            {byPerformer.length > 0 && (
                <section>
                    <h4 className="text-sm font-semibold text-slate-800 mb-3">Nach ausführender Person</h4>
                    <div className="space-y-2">
                        {byPerformer.map((c) => (
                            <div key={c.name} className="flex items-center gap-3 p-3 rounded-xl border border-slate-200">
                                <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center text-sm font-semibold shrink-0">
                                    {c.name.charAt(0)}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs font-semibold text-slate-700 truncate">{c.name}</div>
                                    <div className="text-[11px] text-slate-400">{c.quantity} Einheiten</div>
                                </div>
                                <div className="w-24 shrink-0"><Bar value={c.cents} max={byPerformer[0].cents} color="bg-indigo-400" /></div>
                                <span className="text-xs font-semibold text-slate-700 w-24 text-right shrink-0">{money(c.cents)}</span>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            <section>
                <h4 className="text-sm font-semibold text-slate-800 mb-3">Projekte ({byProject.length})</h4>
                {byProject.length === 0 ? (
                    <div className="py-6 text-center text-slate-400 text-[11px] border border-slate-100 rounded-xl">
                        Keine Leistungen dieser Art im Zeitraum
                    </div>
                ) : (
                    <div className="space-y-2">
                        {byProject.slice(0, 15).map(({ project, cents }) => (
                            <div
                                key={project.id}
                                className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:border-indigo-300 cursor-pointer transition-colors"
                                onClick={() => onSelectProject(project)}
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs font-semibold text-slate-700 truncate">{project.name}</div>
                                    <div className="text-[11px] text-slate-400">{project.responsibleName} · {project.customerName}</div>
                                </div>
                                <span className="text-xs font-semibold text-emerald-700 shrink-0">{money(cents)}</span>
                                <ChevronRight className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </ModalShell>
    );
};
